import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import 'chartjs-adapter-luxon';
import {ConfigServiceProvider} from "../../services/config/config.service.provider";
import {ConfirmationService, MessageService} from "primeng/api";
import {UIChart} from "primeng/chart";
import {TranslateService} from "@ngx-translate/core";
import {CcBaseComponent} from "../../crystal-elements/cc-base.component";
import {MetricsService} from "../../services/metrics/metrics.service";
import {ClearingDataTrendMetricsRequest} from "../../model/metrics/clearing-data-trend-metrics-request";
import {ExchangeVolumePerDateTime} from "../../model/metrics/exchange-volume-per-date-time";
import {SecurityType} from "../../model/metrics/security-type";
import {AppConfigService} from "../../services/app-config.service";
import {EMPTY, exhaustMap, takeUntil, timer} from "rxjs";
import {catchError} from "rxjs/operators";

@Component({
  selector: 'cc-data-trend-chart',
  templateUrl: './data-trend-chart.component.html',
  styleUrls: ['./data-trend-chart.component.scss'],
  providers: [
    ConfigServiceProvider, MessageService,
  ]
})
export class DataTrendChartComponent extends CcBaseComponent implements OnInit {

  @ViewChild('chart') chartRef: UIChart;

  chartData: any;
  chartOptions: any;

  currentRequest: ClearingDataTrendMetricsRequest = {timeInterval: 20, syncMetadata: undefined};

  metrics: ExchangeVolumePerDateTime[] = [];

  transPartKey: string = 'chart.trend';

  selectedExchangeCode: string;

  allExchangeTag: string = 'ALL';

  availableExchanges: string[] = [];

  private clearingStatusLoopSub?: any;

  constructor(cdref: ChangeDetectorRef,
              private metricsService: MetricsService,
              private appConfigService: AppConfigService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.selectedExchangeCode = this.allExchangeTag;
    this.buildChartOptions();
    this.refreshChart();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.startClearingStatusMetricsLoop();
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  startClearingStatusMetricsLoop(): void {
    const intervalMs = this.appConfigService.chartsReloadInterval;

    this.clearingStatusLoopSub?.unsubscribe();

    this.clearingStatusLoopSub = timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        exhaustMap(() =>
          this.metricsService.getClearingDataTrendMetricsList(this.currentRequest).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(metricsResult => {
        if (!metricsResult.success) return;

        if (metricsResult.syncMetadata?.needSync) {
          if (JSON.stringify(this.metrics) !== JSON.stringify(metricsResult.records)) {
            this.metrics = metricsResult.records;
            this.refreshChart();
            this.refreshExchangeCodes();
          }
        }

        this.currentRequest.syncMetadata = metricsResult.syncMetadata;
      });
  }

  private buildChartOptions() {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 200,
      plugins: {
        legend: {
          labels: { color: '#dedede' }
        },
        title: {
          display: true,
          text: this.translateService.instant('page.' + this.transPartKey + '.title') + this.selectedExchangeCode,
          color: '#dedede'
        }
      },
      scales: {
        x: {
          type: 'time',
          time: { tooltipFormat: 'HH:mm:ss', displayFormats: { minute: 'HH:mm', hour: 'HH:mm' } },
          ticks: { color: '#dedede' },
          grid: { display: false }
        },
        y: {
          ticks: { color: '#dedede' },
          grid: { display: false }
        }
      }
    };
  }

  private refreshChart() {
    const tradesData = this.getDataArray(SecurityType.TRADE);
    const ordersData = this.getDataArray(SecurityType.ORDER);
    this.chartOptions.plugins.title.text =
      this.translateService.instant('page.' + this.transPartKey + '.title') + this.selectedExchangeCode;

    if (!this.chartData) {
      this.chartData = {
        datasets: [
          {
            label: 'Trade Volume',
            data: tradesData,
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            pointRadius: 0,
            tension: 0.1,
            fill: false
          },
          {
            label: 'Matched Order Volume',
            data: ordersData,
            borderColor: 'rgba(255, 159, 64, 1)',
            backgroundColor: 'rgba(255, 159, 64, 0.2)',
            pointRadius: 0,
            tension: 0.1,
            fill: false
          }
        ]
      };
      return;
    }

    this.chartData.datasets[0].data = tradesData;
    this.chartData.datasets[1].data = ordersData;
    this.chartRef?.refresh();
  }

  private getDataArray(type: SecurityType): { x: string; y: number }[] {
    const exchangeVolumeData = this.metrics
      .filter(value => value.exchangeCode === this.selectedExchangeCode && value.type === type)
      .sort((a, b) => new Date(a.maxDate).getTime() - new Date(b.maxDate).getTime());

    let runningTotal = 0;
    return exchangeVolumeData.map(dataPoint => {
      runningTotal += dataPoint.volume;
      return { x: new Date(dataPoint.maxDate).toISOString(), y: runningTotal };
    });
  }

  refreshExchangeCodes() {
    this.availableExchanges = [this.allExchangeTag];
    this.metrics.filter(value => value.type === SecurityType.TRADE).forEach(value => {
      if (!this.availableExchanges.includes(value.exchangeCode)) {
        this.availableExchanges.push(value.exchangeCode);
      }
    });
  }

  public selectExchange(exchangeCode: string): void {
    this.selectedExchangeCode = exchangeCode;
    this.refreshChart();
  }
}
